import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import {
  ArrowsUpDownIcon,
  DocumentDuplicateIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import { exposureOptions } from 'data/constants';
import {
  BelowGradeWallType,
  ExposureDirectionType,
  SizingItemsType
} from 'data/types';
import { useState } from 'react';
import { isNotEmpty } from 'utils/helpers';
import {
  getValidSizingAttributeOptions,
  isValidSizingItem
} from 'utils/sizing';
import WallFenestrations from './wall-fenestrations';

type BelowGradeWallProps = {
  wall: BelowGradeWallType;
  updateFn: (wallId: string, updates: Partial<BelowGradeWallType>) => void;
  duplicateFn: (wallId: string) => void;
  deleteFn: (wallId: string) => void;
  switchGradeFn?: (wallId: string) => void;
  switchDisabledReason?: string;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
  defaultExpandAdvanced?: boolean;
  focusFenestrationId?: string;
};

export default function BelowGradeWallComponent({
  wall,
  updateFn,
  duplicateFn,
  deleteFn,
  switchGradeFn,
  switchDisabledReason,
  updateDefaultSizingFn,
  defaultExpandAdvanced = false,
  focusFenestrationId
}: BelowGradeWallProps) {
  const defaultValues = useAppSelector(
    (state) => state.data.defaultSizingItems.belowGradeWall
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(
    defaultExpandAdvanced
  );
  const [animate] = useAutoAnimate({ duration: 100 });
  const [deleteQueued, setDeleteQueued] = useState(false);
  const [switchQueued, setSwitchQueued] = useState(false);
  const sizingItemType = 'belowGradeWalls';
  const childrenValid =
    (wall.verticalGlass || []).every((glass) => glass.valid) &&
    (wall.solidDoors || []).every((door) => door.valid);
  const isModified = Object.keys(defaultValues)
    .filter((key) => isNotEmpty(defaultValues[key]))
    .some((key) => wall[key] !== defaultValues[key]);

  const handleUpdateWithValidation = (changes: Partial<BelowGradeWallType>) => {
    const newWall: BelowGradeWallType = {
      ...wall,
      ...changes
    };
    const isValid = isValidSizingItem(sizingItemType, newWall);

    updateFn(wall.uniqueId, {
      ...newWall,
      valid: isValid
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const handleDuplicateClick = () => {
    duplicateFn(wall.uniqueId);
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteFn(wall.uniqueId);
    setDeleteQueued(false);
  };

  const handleSwitchClick = () => {
    if (!switchQueued) {
      setSwitchQueued(true);
      return;
    }

    switchGradeFn?.(wall.uniqueId);
    setSwitchQueued(false);
  };

  const setAsDefault = () => {
    updateDefaultSizingFn({
      belowGradeWall: {
        ...wall,
        uniqueId: undefined,
        valid: undefined,
        width: undefined,
        height: undefined,
        exposure: undefined,
        verticalGlass: [],
        solidDoors: []
      }
    });
  };

  const classes = clsx(
    'flex flex-col gap-3 rounded-xl border-2 border-gray-200 bg-gray-100',
    wall.valid && childrenValid ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div className={classes}>
      <div
        key={wall.uniqueId}
        className='flex w-full flex-col divide-y divide-gray-300 rounded-lg bg-gray-200 px-5'
        ref={animate}
      >
        <div className='flex min-h-[54px] items-center gap-5'>
          <div className='flex items-center gap-1 font-semibold'>
            {wall.exposure} Wall (Below Grade)
          </div>
          <div className='w-[130px]'>
            <FormInput
              value={wall.width}
              prefix='Width: '
              placeholder='Width (ft)'
              type='number'
              suffix=' ft'
              min={1}
              onChange={(value) => handleUpdateWithValidation({ width: value })}
              required
            />
          </div>
          {!wall.valid && (
            <span className='text-red-500'>Invalid Attributes!</span>
          )}
          {!childrenValid && (
            <span className='text-red-500'>Glass/Doors Invalid!</span>
          )}
          <div className='ml-auto flex items-center gap-2'>
            <button
              className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
              type='button'
              onClick={toggleAdvancedOptions}
            >
              <span>Advanced</span>
              {showAdvancedOptions ? (
                <ChevronUpIcon className='h-5 w-5' />
              ) : (
                <ChevronDownIcon className='h-5 w-5' />
              )}
              <Tooltip
                text={
                  showAdvancedOptions
                    ? 'Hide Advanced Options'
                    : 'Show Advanced Options'
                }
              />
            </button>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDuplicateClick}
            >
              <DocumentDuplicateIcon className='h-5 w-5' />
              <Tooltip text='Duplicate Wall' />
            </button>
            {switchGradeFn && (
              <button
                className={clsx(
                  'relative h-full text-gray-500',
                  switchDisabledReason
                    ? 'cursor-not-allowed opacity-40'
                    : 'cursor-pointer hover:text-gray-700'
                )}
                type='button'
                disabled={!!switchDisabledReason}
                onClick={switchDisabledReason ? undefined : handleSwitchClick}
                onMouseLeave={() => setSwitchQueued(false)}
              >
                <ArrowsUpDownIcon className='h-5 w-5' />
                <Tooltip
                  text={
                    switchDisabledReason ||
                    (switchQueued
                      ? 'Click Again to Confirm (resets attributes)'
                      : 'Switch to Above Grade')
                  }
                />
              </button>
            )}
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDeleteClick}
              onMouseLeave={() => setDeleteQueued(false)}
            >
              <TrashIcon className='h-5 w-5' />
              <Tooltip
                text={deleteQueued ? 'Click Again to Confirm' : 'Delete Wall'}
              />
            </button>
          </div>
        </div>
        {showAdvancedOptions && (
          <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
            <FormInput
              value={wall.exposure}
              options={exposureOptions}
              onChange={(value) =>
                handleUpdateWithValidation({
                  exposure: value as ExposureDirectionType
                })
              }
              label='Direction Facing'
              type='select'
              required
              preventDeselection
            />
            <FormInput
              value={wall.belowGradeDepth}
              label='Depth Below Grade'
              subLabel='(ft)'
              type='select'
              onChange={(value) => {
                handleUpdateWithValidation({
                  belowGradeDepth: value
                });
              }}
              options={[2, 4, 6, 8, 10].map((depth) => ({
                value: depth,
                label: `${depth} ft`
              }))}
              enableChips
              required
              preventDeselection
              tooltip='Distance from bottom of wall to ground level'
            />
            <div className='w-[80px]'>
              <FormInput
                value={wall.height}
                label='Height'
                subLabel='(ft)'
                type='number'
                suffix=' ft'
                min={1}
                onChange={(value) => {
                  handleUpdateWithValidation({
                    height: value
                  });
                }}
                required
              />
            </div>
            <FormInput
              value={wall.insulationType}
              label='Insulation Type'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                wall,
                'insulationType'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  insulationType: value,
                  studMaterial: value.includes('Cavity') ? 'Wood' : undefined,
                  insulationCoverage:
                    value === 'None' ? 'None' : 'Sill Plate to Floor',
                  insulationValue:
                    value === 'None' ? 'None' : wall.insulationValue
                });
              }}
              required
              preventDeselection
            />
            {wall.insulationType !== 'None' && (
              <>
                <FormInput
                  value={wall.insulationValue}
                  label='Insulation Value'
                  type='select'
                  options={getValidSizingAttributeOptions(
                    sizingItemType,
                    wall,
                    'insulationValue'
                  )}
                  onChange={(value) => {
                    handleUpdateWithValidation({
                      insulationValue: value
                    });
                  }}
                  required
                  preventDeselection
                />
                <FormInput
                  value={wall.insulationCoverage}
                  label='Insulation Coverage'
                  type='select'
                  options={getValidSizingAttributeOptions(
                    sizingItemType,
                    wall,
                    'insulationCoverage'
                  )}
                  onChange={(value) => {
                    handleUpdateWithValidation({
                      insulationCoverage: value
                    });
                  }}
                  required
                  preventDeselection
                />
              </>
            )}
            {isModified && (
              <div className='flex w-full justify-end'>
                <button
                  className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
                  type='button'
                  onClick={setAsDefault}
                >
                  <span>Set as Default</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <WallFenestrations
        wallType='Below Grade'
        wall={wall}
        updateFn={updateFn}
        updateDefaultSizingFn={updateDefaultSizingFn}
        focusFenestrationId={focusFenestrationId}
      />
    </div>
  );
}
